import { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth/context/AuthContext';
import { User } from '@/types';
import api from '@/utils/api.utils';
import { showAuthError, ToastService } from '@/utils/toast';
import { OnboardingStorage } from '@/utils/onboarding.utils';

interface OnboardingProps {
  user: User;
  onComplete: () => void;
}

interface OnboardingData {
  themePreference: string;
  name: string;
  dateOfBirth: {
    day: string;
    month: string;
    year: string;
  };
  preferredLanguage: string;
  userType: string;
  userTypeOther: string;
  discoverySource: string;
  discoverySourceOther: string;
  interests: string[];
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const LANGUAGES = [
  { code: 'English', name: 'English', flag: '🇺🇸' },
];

const USER_TYPES = [
  { value: 'student', label: 'I am a student', icon: '🎓' },
  { value: 'educator', label: 'I am an educator', icon: '👨‍🏫' },
  { value: 'parent', label: 'I am a parent', icon: '👨‍👩‍👧‍👦' },
  { value: 'other', label: 'Other', icon: '···' },
];

const DISCOVERY_SOURCES = [
  { value: 'tiktok', label: 'TikTok', icon: '🎵' },
  { value: 'instagram', label: 'Instagram', icon: '📷' },
  { value: 'reddit', label: 'Reddit', icon: '🟠' },
  { value: 'discord', label: 'Discord', icon: '💬' },
  { value: 'teacher', label: 'Teacher/Counselor', icon: '👨‍🏫' },
  { value: 'school', label: 'School', icon: '🏫' },
  { value: 'google', label: 'Google', icon: '🔍' },
  { value: 'referral', label: 'Student Referral', icon: '👥' },
  { value: 'other', label: 'Other', icon: '···' },
];

const INTERESTS = [
  { value: 'college_finder', label: 'Find colleges that fit my preferences', icon: '🎯' },
  { value: 'essay_feedback', label: 'Get expert-reviewed feedback on my essay', icon: '📝' },
  { value: 'notifications', label: 'Get text notifications for upcoming deadlines and more', icon: '📱' },
  { value: 'counselors', label: 'Connect with live counselors for 1-on-1 guidance', icon: '👥' },
];

const Onboarding: React.FC<OnboardingProps> = ({ user, onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { updateUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('onboarding_current_step');
    const step = savedStep ? parseInt(savedStep, 10) : 0;
    return step >= 0 && step <= 5 ? step : 0;
  });
  
  const [data, setData] = useState<OnboardingData>(() => {
    const savedData = localStorage.getItem('onboarding_data');
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (error) {
        console.warn('Failed to parse saved onboarding data');
      }
    }
    return {
      themePreference: 'Light',
      name: user.name || '',
      dateOfBirth: { day: '', month: '', year: '' },
      preferredLanguage: 'English',
      userType: '',
      userTypeOther: '',
      discoverySource: '',
      discoverySourceOther: '',
      interests: []
    };
  });

  useEffect(() => {
    localStorage.setItem('onboarding_current_step', currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem('onboarding_data', JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    const savedUserEmail = localStorage.getItem('onboarding_user_email');
    if (savedUserEmail && savedUserEmail !== user.email) {
      localStorage.removeItem('onboarding_current_step');
      localStorage.removeItem('onboarding_data');
      localStorage.removeItem('onboarding_user_email');
    }
    localStorage.setItem('onboarding_user_email', user.email);
  }, [user.email]);

  const update = (field: keyof OnboardingData, value: any) => {
    setData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'userType' && value !== 'other') {
        newData.userTypeOther = '';
      }
      if (field === 'discoverySource' && value !== 'other') {
        newData.discoverySourceOther = '';
      }
      return newData;
    });
  };

  const toggleInterest = (interest: string) => {
    setData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const isValid = (step: number) => {
    switch (step) {
      case 0: return data.themePreference !== '';
      case 1: return data.name.trim() && data.dateOfBirth.day && data.dateOfBirth.month && data.dateOfBirth.year && data.preferredLanguage;
      case 2: return data.userType !== '' && (data.userType !== 'other' || data.userTypeOther.trim() !== '');
      case 3: return data.discoverySource !== '' && (data.discoverySource !== 'other' || data.discoverySourceOther.trim() !== '');
      case 4: return true;
      case 5: return true;
      default: return false;
    }
  };

  const next = () => currentStep < 5 && isValid(currentStep) && setCurrentStep(currentStep + 1);
  const back = () => currentStep > 0 && setCurrentStep(currentStep - 1);

  const complete = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      let dateOfBirth = null;
      if (data.dateOfBirth.day && data.dateOfBirth.month && data.dateOfBirth.year) {
        dateOfBirth = `${data.dateOfBirth.year}-${String(MONTHS.indexOf(data.dateOfBirth.month) + 1).padStart(2, '0')}-${String(data.dateOfBirth.day).padStart(2, '0')}`;
      }
      
      const payload: any = {};
      
      if (dateOfBirth) payload.date_of_birth = dateOfBirth;
      if (data.preferredLanguage) payload.preferred_language = data.preferredLanguage;
      if (data.themePreference) payload.theme_preference = data.themePreference;
      if (data.userType) {
        payload.user_type = data.userType === 'other' ? data.userTypeOther : data.userType;
      }
      if (data.discoverySource) {
        payload.discovery_source = data.discoverySource === 'other' ? data.discoverySourceOther : data.discoverySource;
      }
      if (data.interests && data.interests.length > 0) payload.interests = JSON.stringify(data.interests);
      
      await api.patch('/api/v1/auth/me', payload);
      
      updateUser({ 
        ...user, 
        is_new_user: false,
        date_of_birth: dateOfBirth || undefined,
        preferred_language: data.preferredLanguage,
        theme_preference: data.themePreference,
        user_type: data.userType === 'other' ? data.userTypeOther : data.userType,
        discovery_source: data.discoverySource === 'other' ? data.discoverySourceOther : data.discoverySource,
        interests: data.interests.length > 0 ? JSON.stringify(data.interests) : undefined
      });
      
      localStorage.removeItem('onboarding_current_step');
      localStorage.removeItem('onboarding_data');
      localStorage.removeItem('onboarding_user_email');
      OnboardingStorage.markOnboardingCompleted(user.email);
      
      ToastService.showSuccess('Welcome to Apply.AI!');
      onComplete();
    } catch (error) {
      showAuthError('Failed to complete onboarding. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const dots = () => (
    <div className="flex justify-center mt-6 space-x-2">
      {[0,1,2,3,4,5].map(i => <div key={i} className={`w-2 h-2 rounded-full ${i === currentStep ? 'bg-[var(--text-primary)]' : 'bg-[var(--border-primary)]'}`} />)}
    </div>
  );

  const steps = [
    <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
      <div className="mb-8 text-center">
        <div className="flex justify-center mx-auto mb-4">
          <img
            src="/logo.png"
            alt="Apply.AI Logo"
            className="h-16 max-w-[120px]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('logo.png')) {
                target.src = '/logo_svg.svg';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback text-xl font-bold text-blue-600';
                  fallback.textContent = 'Apply.AI';
                  parent.appendChild(fallback);
                }
              }
            }}
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Choose your style</h1>
      </div>
      <div className="grid grid-cols-2 gap-6 mb-8">
        {[
          { value: 'Light', preview: <div className="bg-gray-100 rounded-lg p-4 h-32 flex items-center justify-center"><div className="space-y-2 w-full"><div className="h-2 bg-gray-300 rounded w-3/4"></div><div className="h-2 bg-gray-300 rounded w-1/2"></div><div className="h-2 bg-gray-300 rounded w-2/3"></div></div></div> },
          { value: 'Dark', preview: <div className="bg-gray-800 rounded-lg p-4 h-32 flex items-center justify-center"><div className="space-y-2 w-full"><div className="h-2 bg-gray-600 rounded w-3/4"></div><div className="h-2 bg-gray-600 rounded w-1/2"></div><div className="h-2 bg-gray-600 rounded w-2/3"></div><div className="w-3 h-3 bg-green-400 rounded-full ml-auto"></div></div></div> }
        ].map(theme => (
          <div key={theme.value} onClick={() => update('themePreference', theme.value)} className={`p-6 border-2 rounded-2xl cursor-pointer transition-all ${data.themePreference === theme.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            {theme.preview}
            <p className="text-center font-medium mt-4">{theme.value}</p>
          </div>
        ))}
      </div>
      <button onClick={next} disabled={!isValid(0)} className={`w-full ${!isValid(0) ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-black hover:bg-gray-800 text-white'} font-medium text-base rounded-lg h-10 transition-all`}>Next</button>
      {dots()}
    </div>,

    <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
      <div className="mb-8 text-center">
        <div className="flex justify-center mx-auto mb-4">
          <img
            src="/logo.png"
            alt="Apply.AI Logo"
            className="h-16 max-w-[120px]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('logo.png')) {
                target.src = '/logo_svg.svg';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback text-xl font-bold text-blue-600';
                  fallback.textContent = 'Apply.AI';
                  parent.appendChild(fallback);
                }
              }
            }}
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Help us personalize your experience</h1>
      </div>
      <div className="space-y-6 mb-8">
        <div>
          <label className="block text-lg font-medium text-gray-900 mb-3">What's your name?</label>
          <input type="text" value={data.name} onChange={(e) => update('name', e.target.value)} placeholder="Robert" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-lg focus:border-blue-500 focus:outline-none transition-colors" />
        </div>
        <div>
          <label className="block text-lg font-medium text-gray-900 mb-3">What's your date of birth?</label>
          <input 
            type="date" 
            value={data.dateOfBirth.day && data.dateOfBirth.month && data.dateOfBirth.year ? 
              `${data.dateOfBirth.year}-${String(MONTHS.indexOf(data.dateOfBirth.month) + 1).padStart(2, '0')}-${String(data.dateOfBirth.day).padStart(2, '0')}` : 
              ''
            }
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => {
              if (e.target.value) {
                const [year, month, day] = e.target.value.split('-');
                setData(prev => ({
                  ...prev,
                  dateOfBirth: {
                    day,
                    month: MONTHS[parseInt(month) - 1],
                    year
                  }
                }));
              }
            }}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-lg focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>
        <div>
          <label className="block text-lg font-medium text-gray-900 mb-3">What's your preferred language?</label>
          <select value={data.preferredLanguage} onChange={(e) => update('preferredLanguage', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-lg focus:border-blue-500 focus:outline-none transition-colors">
            {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.flag} {lang.name}</option>)}
          </select>
        </div>
      </div>
      <button onClick={next} disabled={!isValid(1)} className={`w-full ${!isValid(1) ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-black hover:bg-gray-800 text-white'} font-medium text-base rounded-lg h-10 transition-all`}>Next</button>
      {dots()}
    </div>,

    <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
      <div className="mb-8 text-center">
        <div className="flex justify-center mx-auto mb-4">
          <img
            src="/logo.png"
            alt="Apply.AI Logo"
            className="h-16 max-w-[120px]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('logo.png')) {
                target.src = '/logo_svg.svg';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback text-xl font-bold text-blue-600';
                  fallback.textContent = 'Apply.AI';
                  parent.appendChild(fallback);
                }
              }
            }}
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Which one describes you the best?</h1>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {USER_TYPES.map(type => (
          <div key={type.value} onClick={() => update('userType', type.value)} className={`p-6 border-2 rounded-2xl cursor-pointer transition-all text-center ${data.userType === type.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="text-4xl mb-4">{type.icon}</div>
            <p className="font-medium">{type.label}</p>
          </div>
        ))}
      </div>
      {data.userType === 'other' && (
        <div className="mb-6">
          <input
            type="text"
            placeholder="Please specify..."
            value={data.userTypeOther}
            onChange={(e) => update('userTypeOther', e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>
      )}
      <div className="flex justify-between">
        <button onClick={back} className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors">Back</button>
        <button onClick={next} disabled={!isValid(2)} className={`px-6 py-2 ${!isValid(2) ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-gray-500 hover:bg-gray-600 text-white'} font-medium text-base rounded-lg transition-all`}>Next</button>
      </div>
      {dots()}
    </div>,

    <div className="rounded-3xl shadow-2xl p-6 max-h-[600px] overflow-hidden" style={{ background: 'var(--surface-primary)' }}>
      <div className="mb-6 text-center">
        <div className="flex justify-center mx-auto mb-4">
          <img
            src="/logo.png"
            alt="Apply.AI Logo"
            className="h-12 max-w-[100px]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('logo.png')) {
                target.src = '/logo_svg.svg';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback text-lg font-bold text-blue-600';
                  fallback.textContent = 'Apply.AI';
                  parent.appendChild(fallback);
                }
              }
            }}
          />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">How did you hear about us?</h1>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-6 max-h-[300px] overflow-y-auto">
        {DISCOVERY_SOURCES.map(source => (
          <div key={source.value} onClick={() => update('discoverySource', source.value)} className={`p-3 border-2 rounded-xl cursor-pointer transition-all text-center h-20 flex flex-col justify-center items-center ${data.discoverySource === source.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="text-lg mb-1">{source.icon}</div>
            <p className="text-xs font-medium leading-none">{source.label}</p>
          </div>
        ))}
      </div>
      {data.discoverySource === 'other' && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Please specify..."
            value={data.discoverySourceOther}
            onChange={(e) => update('discoverySourceOther', e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>
      )}
      <div className="flex justify-between items-center mt-auto">
        <button onClick={back} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">Back</button>
        <button onClick={next} disabled={!isValid(3)} className={`px-6 py-2 ${!isValid(3) ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-gray-500 hover:bg-gray-600 text-white'} font-medium text-base rounded-lg transition-all`}>Next</button>
      </div>
      {dots()}
    </div>,

    <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
      <div className="mb-8 text-center">
        <div className="flex justify-center mx-auto mb-4">
          <img
            src="/logo.png"
            alt="Apply.AI Logo"
            className="h-16 max-w-[120px]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('logo.png')) {
                target.src = '/logo_svg.svg';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback text-xl font-bold text-blue-600';
                  fallback.textContent = 'Apply.AI';
                  parent.appendChild(fallback);
                }
              }
            }}
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">What would you like to do with collegeapply.ai?</h1>
        <p className="text-gray-600">Select all that apply</p>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {INTERESTS.map(interest => (
          <div key={interest.value} onClick={() => toggleInterest(interest.value)} className={`p-6 border-2 rounded-2xl cursor-pointer transition-all text-center ${data.interests.includes(interest.value) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="text-4xl mb-4">{interest.icon}</div>
            <p className="font-medium text-sm">{interest.label}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-between">
        <button onClick={back} className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors">Back</button>
        <button onClick={next} className="px-8 py-3 text-gray-600 hover:text-gray-800 transition-colors">
          {data.interests.length > 0 ? 'Next' : 'Skip'}
        </button>
      </div>
      {dots()}
    </div>,

    <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
      <div className="mb-8 text-center">
        <div className="flex justify-center mx-auto mb-4">
          <img
            src="/logo.png"
            alt="Apply.AI Logo"
            className="h-16 max-w-[120px]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('logo.png')) {
                target.src = '/logo_svg.svg';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'logo-fallback text-xl font-bold text-blue-600';
                  fallback.textContent = 'Apply.AI';
                  parent.appendChild(fallback);
                }
              }
            }}
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome to collegeapply.ai</h1>
        <p className="text-gray-600 mb-8">Please complete our short (10 minutes) applicant quiz so that we can start recommending colleges to you and improve your overall experience.</p>
      </div>
      <div className="space-y-4 mb-8">
        <button onClick={complete} disabled={isLoading} className={`w-full ${isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white font-medium text-base rounded-lg h-10 flex items-center justify-center transition-all`}>
          {isLoading ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Setting up...</>) : 'Start Quiz'}
        </button>
        <button onClick={complete} className="w-full border-2 border-gray-200 text-gray-600 hover:text-gray-800 font-medium text-base rounded-lg h-10 transition-all">I'll do it later</button>
      </div>
      {dots()}
    </div>
  ];

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {steps[currentStep]}
      </div>
    </div>
  );
};

export default Onboarding;